import React, { useState } from "react";
import { useUploadStore, UploadItem } from "@presentation/stores/UploadStore";
import { ProgressBar } from "@progress/kendo-react-progressbars";
import { Button } from "@progress/kendo-react-buttons";
import { chevronDownIcon, chevronUpIcon, xIcon, fileIcon, uploadIcon, checkIcon, cancelIcon, exclamationCircleIcon } from "@progress/kendo-svg-icons";
import { SvgIcon } from "@progress/kendo-react-common";

const formatSpeed = (bytesPerSecond: number) => {
  if (bytesPerSecond === 0) return "0 B/s";
  const k = 1024;
  const sizes = ["B/s", "KB/s", "MB/s", "GB/s"];
  const index = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
  return Number((bytesPerSecond / Math.pow(k, index)).toFixed(2)) + " " + sizes[index];
};

const formatSize = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.floor(Math.log(bytes) / Math.log(k));
  return Number((bytes / Math.pow(k, index)).toFixed(2)) + " " + sizes[index];
};

const renderUploadStatus = (upload: UploadItem) => {
  if (upload.status === "completed") {
    return (
      <span style={{ color: "green", display: "flex", alignItems: "center", gap: "3px" }}>
        <SvgIcon icon={checkIcon} size="xsmall" /> Completed
      </span>
    );
  }

  if (upload.status === "failed") {
    return (
      <span style={{ color: "red", display: "flex", alignItems: "center", gap: "3px" }} title={upload.error}>
        <SvgIcon icon={exclamationCircleIcon} size="xsmall" /> Failed
      </span>
    );
  }

  return upload.status === "cancelled" ? "Cancelled" : `${Math.round(upload.progress.percent)}% • ${formatSpeed(upload.progress.speed)}`;
};

export const UploadProgressIndicator: React.FC = () => {
  const { activeUploads, cancelUpload, removeUpload } = useUploadStore();
  const uploads = Object.values(activeUploads);
  const [isExpanded, setIsExpanded] = useState(true);

  if (uploads.length === 0) return null;

  const uploadingCount = uploads.filter((u) => u.status === "uploading" || u.status === "pending").length;

  return (
    <div
      className="upload-progress-container"
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        width: "350px",
        backgroundColor: "white",
        boxShadow: "0 0 10px rgba(0,0,0,0.2)",
        borderRadius: "8px",
        zIndex: 10_000,
        overflow: "hidden",
        border: "1px solid #ddd",
      }}
    >
      <div
        className="upload-header"
        style={{
          padding: "10px 15px",
          backgroundColor: "#f5f5f5",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          borderBottom: isExpanded ? "1px solid #ddd" : "none",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SvgIcon icon={uploadIcon} />
          <span style={{ fontWeight: "bold" }}>{uploadingCount > 0 ? `Uploading ${uploadingCount} files...` : "Uploads complete"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <SvgIcon icon={isExpanded ? chevronDownIcon : chevronUpIcon} />
          {uploadingCount === 0 && (
            <Button
              fillMode="flat"
              title="Clear all"
              onClick={(e) => {
                e.stopPropagation();
                for (const u of uploads) {
                  removeUpload(u.id);
                }
              }}
            >
              <SvgIcon icon={xIcon} />
            </Button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div
          className="upload-list"
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            padding: "10px",
          }}
        >
          {uploads.map((upload: UploadItem) => (
            <div
              key={upload.id}
              style={{
                marginBottom: "15px",
                padding: "10px",
                backgroundColor: "#f9f9f9",
                borderRadius: "4px",
                position: "relative",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "5px", overflow: "hidden" }}>
                  <SvgIcon icon={fileIcon} size="small" />
                  <span
                    style={{
                      fontSize: "0.9rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "200px",
                    }}
                    title={upload.fileName}
                  >
                    {upload.fileName}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {upload.status === "uploading" || upload.status === "pending" ? (
                    <Button fillMode="flat" title="Cancel Upload" onClick={() => cancelUpload(upload.id)}>
                      <SvgIcon icon={cancelIcon} size="small" />
                    </Button>
                  ) : (
                    <Button fillMode="flat" title="Remove from list" onClick={() => removeUpload(upload.id)}>
                      <SvgIcon icon={xIcon} size="small" />
                    </Button>
                  )}
                </div>
              </div>

              <ProgressBar value={upload.progress.percent} style={{ height: "4px", marginBottom: "5px" }} />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.75rem",
                  color: "#666",
                }}
              >
                <span>{renderUploadStatus(upload)}</span>
                <span>
                  {formatSize(upload.progress.loaded)} / {formatSize(upload.progress.total)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
